import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Users, BookOpen, Target, ArrowDown, Send, Plus, X, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/apiRecovery';

const API_URL = window.location.origin;

// ── Ordenação oficial: 1º..9º ano → Ensino Médio; A,B,C; EF1<EF2<EM
const SEG_RANK = { EF1: 0, EF2: 1, EM: 2 };

function inferSegmentRank(name = '') {
  const n = (name || '').toUpperCase();
  if (/M[ÉE]DIO|^EM\b|3º\s*MÉDIO|2º\s*MÉDIO|1º\s*MÉDIO/.test(n)) return SEG_RANK.EM;
  if (/^[6789]º|6º\s*ANO|7º\s*ANO|8º\s*ANO|9º\s*ANO/.test(n)) return SEG_RANK.EF2;
  return SEG_RANK.EF1;
}
function inferAnoNumber(name = '') {
  const m = (name || '').match(/(\d+)\s*º?\s*(ano|m[ée]dio|a)/i) || (name || '').match(/^(\d+)/);
  if (m) return parseInt(m[1], 10);
  return 99;
}
function inferLetra(name = '') {
  const m = (name || '').match(/\b([A-Z])\b\s*$/);
  return m ? m[1] : 'Z';
}
function sortTurmasOficial(turmas = []) {
  return [...turmas].sort((a, b) => {
    const an = inferAnoNumber(a.name || a.nome || ''); const bn = inferAnoNumber(b.name || b.nome || '');
    if (an !== bn) return an - bn;
    const al = inferLetra(a.name || a.nome || ''); const bl = inferLetra(b.name || b.nome || '');
    if (al !== bl) return al.localeCompare(bl);
    const ar = inferSegmentRank(a.name || a.nome || ''); const br = inferSegmentRank(b.name || b.nome || '');
    return ar - br;
  });
}

const STUDY_TIPOS = [
  { value: 'capitulo', label: 'Capítulo' },
  { value: 'pagina', label: 'Página' },
  { value: 'tema', label: 'Tema' },
  { value: 'lista_exercicios', label: 'Lista de exercícios' },
  { value: 'observacao', label: 'Observação' },
];

export function RelatorioRecuperacao({ year, filtros, onSelectAluno }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trimestre, setTrimestre] = useState('');
  const [turmaId, setTurmaId] = useState('');

  // Seleção
  const [selected, setSelected] = useState({}); // {[idx]: true}
  const [classroomTarget, setClassroomTarget] = useState(null); // {alunos:[...], class_group_id, subject_id, trimester}

  useEffect(() => { loadData(); }, [year, trimestre, turmaId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { year: Number(year) };
      if (trimestre) params.trimestre = trimestre;
      if (turmaId) params.class_group_id = turmaId;
      // Usa o mesmo apiClient do Portal Equipe (baseURL + Authorization automáticos)
      // eslint-disable-next-line no-console
      console.log('[RelatorioRecuperacao] GET /central-notas/relatorio-recuperacao', params);
      const res = await api.get('/central-notas/relatorio-recuperacao', { params, timeout: 90000 });
      setData(res.data || { alunos: [], estatisticas: {} });
      setSelected({});
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const detailMsg = (typeof detail === 'string') ? detail : (detail?.message || null);
      let msg;
      if (status === 401) msg = 'Sua sessão expirou. Faça login novamente.';
      else if (status === 403) msg = detailMsg || 'Sem permissão para acessar este relatório.';
      else if (status === 404) msg = 'Endpoint /api/central-notas/relatorio-recuperacao não encontrado.';
      else if (status >= 500) msg = `Erro ${status} no servidor: ${detailMsg || 'tente novamente em alguns segundos.'}`;
      else if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) msg = 'A consulta demorou demais. Filtre por trimestre/turma e tente novamente.';
      else if (!err?.response) msg = 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
      else msg = detailMsg || err?.message || 'Erro ao carregar relatório.';
      // eslint-disable-next-line no-console
      console.error('[RelatorioRecuperacao] erro de carga', { url: err?.config?.url, method: err?.config?.method, status, code: err?.code, message: err?.message, detail });
      toast.error(msg, { duration: 9000 });
      setData({ alunos: [], estatisticas: {} });
    } finally {
      setLoading(false);
    }
  };

  const turmasOrdenadas = useMemo(() => sortTurmasOficial(filtros?.turmas || []), [filtros?.turmas]);

  const alunosVisiveis = useMemo(() => (data?.alunos || []).slice(0, 50), [data]);

  const todasSelecionadas = alunosVisiveis.length > 0 && alunosVisiveis.every((_, i) => selected[i]);
  const algumasSelecionadas = alunosVisiveis.some((_, i) => selected[i]);

  const toggleAll = (checked) => {
    const next = {};
    if (checked) alunosVisiveis.forEach((_, i) => { next[i] = true; });
    setSelected(next);
  };

  const toggleOne = (idx, checked) => setSelected(prev => ({ ...prev, [idx]: checked }));

  const getSelecionados = () => alunosVisiveis.filter((_, i) => selected[i]);

  const abrirEnvioLote = () => {
    const sel = getSelecionados();
    if (!sel.length) { toast.error('Selecione pelo menos um aluno.'); return; }
    // Validação: todos devem ser da MESMA turma + disciplina + trimestre
    const t = sel[0];
    const mismatch = sel.some(a => a.turma_id !== t.turma_id || a.disciplina_id !== t.disciplina_id || a.trimestre !== t.trimestre);
    if (mismatch) {
      toast.error('Selecione alunos da mesma turma, disciplina e trimestre.');
      return;
    }
    setClassroomTarget({
      alunos: sel,
      class_group_id: t.turma_id,
      class_group_name: t.turma_nome,
      subject_id: t.disciplina_id,
      subject_name: t.disciplina_nome,
      trimester: t.trimestre,
    });
  };

  const abrirEnvioLinha = (aluno) => {
    setClassroomTarget({
      alunos: [aluno],
      class_group_id: aluno.turma_id,
      class_group_name: aluno.turma_nome,
      subject_id: aluno.disciplina_id,
      subject_name: aluno.disciplina_nome,
      trimester: aluno.trimestre,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  if (!data) return null;

  const { estatisticas, alunos, por_disciplina, por_turma } = data;
  const selCount = getSelecionados().length;

  return (
    <div className="space-y-6" data-testid="relatorio-recuperacao">
      {/* Filtros */}
      <Card className="border-orange-100 bg-orange-50">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-medium text-orange-700">Relatório de Recuperação</span>
            </div>
            <Select value={trimestre || 'all'} onValueChange={(v) => setTrimestre(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] bg-white border-orange-200" data-testid="rec-trimestre-filter"><SelectValue placeholder="Todos os Trimestres" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todos os Trimestres</SelectItem>
                <SelectItem value="1º Trimestre">1º Trimestre</SelectItem>
                <SelectItem value="2º Trimestre">2º Trimestre</SelectItem>
                <SelectItem value="3º Trimestre">3º Trimestre</SelectItem>
              </SelectContent>
            </Select>
            <Select value={turmaId || 'all'} onValueChange={(v) => setTurmaId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px] bg-white border-orange-200" data-testid="rec-turma-filter"><SelectValue placeholder="Todas as Turmas" /></SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                <SelectItem value="all">Todas as Turmas</SelectItem>
                {turmasOrdenadas.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
              </SelectContent>
            </Select>

            <div className="ml-auto flex items-center gap-2">
              <Badge variant="outline" className="border-orange-300 text-orange-700">
                {selCount} selecionado{selCount === 1 ? '' : 's'}
              </Badge>
              <Button
                size="sm"
                onClick={abrirEnvioLote}
                disabled={selCount === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                data-testid="rec-enviar-selecionados-btn"
              >
                <Send className="w-4 h-4" />
                Enviar selecionados para Classroom
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estatísticas Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2"><Users className="w-5 h-5 opacity-80" /><span className="text-sm opacity-80">Alunos em Risco</span></div>
            <div className="text-4xl font-bold mt-2">{estatisticas.total_alunos_risco}</div>
            <div className="text-sm opacity-70">precisam de atenção especial</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 opacity-80" /><span className="text-sm opacity-80">Disciplinas Afetadas</span></div>
            <div className="text-4xl font-bold mt-2">{estatisticas.total_disciplinas_risco}</div>
            <div className="text-sm opacity-70">com alunos abaixo da média</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-pink-500 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2"><Target className="w-5 h-5 opacity-80" /><span className="text-sm opacity-80">Total de Registros</span></div>
            <div className="text-4xl font-bold mt-2">{estatisticas.total_registros}</div>
            <div className="text-sm opacity-70">notas abaixo de 6.0</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-orange-100">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><BookOpen className="w-5 h-5 text-orange-600" />Disciplinas Críticas</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={por_disciplina.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="nome" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, n) => [v, n === 'alunos_risco' ? 'Alunos' : 'Média']} contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }} />
                  <Bar dataKey="alunos_risco" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-100">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-orange-600" />Turmas com Mais Alunos em Risco</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={por_turma.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip formatter={(v) => [v, 'Alunos em Risco']} contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }} />
                  <Bar dataKey="alunos_risco" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Alunos */}
      <Card className="border-orange-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><ArrowDown className="w-5 h-5 text-orange-600" />Alunos que Precisam de Recuperação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto dna-responsive-table sticky-first-col">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={todasSelecionadas}
                      onCheckedChange={toggleAll}
                      aria-label="Selecionar todos"
                      data-testid="rec-select-all"
                      data-state={todasSelecionadas ? 'checked' : (algumasSelecionadas ? 'indeterminate' : 'unchecked')}
                    />
                  </TableHead>
                  <TableHead>Aluno</TableHead>
                  <TableHead>Turma</TableHead>
                  <TableHead>Disciplina</TableHead>
                  <TableHead>Trimestre</TableHead>
                  <TableHead className="text-center">Média</TableHead>
                  <TableHead className="text-center">Nota Recup.</TableHead>
                  <TableHead className="text-center">Pós-Rec.</TableHead>
                  <TableHead className="text-center">Pontos Necessários</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alunosVisiveis.map((aluno, idx) => {
                  const media = Number(aluno.media_antes_rec ?? aluno.media ?? 0);
                  const notaRec = aluno.nota_recuperacao;
                  const posRec = aluno.media_pos_rec;
                  const situ = aluno.situacao_final || (aluno.status === 'critico' ? 'Crítico' : 'Atenção');
                  const situClass = situ === 'Recuperado'
                    ? 'bg-emerald-500'
                    : (situ === 'Permanece em recuperação' ? 'bg-red-500'
                    : (situ === 'Aguardando nota de recuperação' ? 'bg-amber-500' : 'bg-yellow-500'));
                  return (
                  <TableRow key={idx} className="hover:bg-orange-50">
                    <TableCell>
                      <Checkbox
                        checked={!!selected[idx]}
                        onCheckedChange={(c) => toggleOne(idx, !!c)}
                        aria-label={`Selecionar ${aluno.aluno_nome}`}
                        data-testid={`rec-select-row-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium cursor-pointer" onClick={() => onSelectAluno?.(aluno.aluno_id)} data-testid={`rec-row-aluno-${idx}`}>{aluno.aluno_nome}</TableCell>
                    <TableCell className="text-sm text-slate-600">{aluno.turma_nome}</TableCell>
                    <TableCell className="text-sm">{aluno.disciplina_nome}</TableCell>
                    <TableCell className="text-sm text-slate-500">{aluno.trimestre}</TableCell>
                    <TableCell className="text-center">
                      <span className={`font-bold ${aluno.status === 'critico' ? 'text-red-600' : 'text-yellow-600'}`} data-testid={`rec-row-media-${idx}`}>{media.toFixed(1)}</span>
                    </TableCell>
                    <TableCell className="text-center" data-testid={`rec-row-nota-rec-${idx}`}>
                      {notaRec == null ? <span className="text-slate-400">—</span> : <span className="font-semibold text-purple-700">{Number(notaRec).toFixed(1)}</span>}
                    </TableCell>
                    <TableCell className="text-center" data-testid={`rec-row-pos-rec-${idx}`}>
                      {posRec == null ? <span className="text-slate-400">—</span> : <span className="font-bold text-emerald-700">{Number(posRec).toFixed(1)}</span>}
                    </TableCell>
                    <TableCell className="text-center"><Badge variant="outline" className="border-orange-300 text-orange-700">+{Number(aluno.pontos_necessarios ?? 0).toFixed(1)}</Badge></TableCell>
                    <TableCell><Badge className={situClass} data-testid={`rec-row-situacao-${idx}`}>{situ}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="gap-1 text-purple-700 border-purple-300 hover:bg-purple-50" onClick={() => abrirEnvioLinha(aluno)} data-testid={`rec-enviar-row-${idx}`}>
                        <Send className="w-3.5 h-3.5" />
                        Classroom
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {alunos.length > 50 && (<div className="text-center mt-4 text-sm text-slate-500">Mostrando 50 de {alunos.length} registros</div>)}
        </CardContent>
      </Card>

      {classroomTarget && (
        <ClassroomEnvioModal
          target={classroomTarget}
          onClose={() => setClassroomTarget(null)}
          onSent={() => { setClassroomTarget(null); setSelected({}); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MODAL: Envio para Classroom
// ─────────────────────────────────────────────────────────────────
function ClassroomEnvioModal({ target, onClose, onSent }) {
  const [title, setTitle] = useState(`Recuperação - ${target.subject_name || ''} (${target.trimester || ''})`.trim());
  const [instructions, setInstructions] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [contents, setContents] = useState([{ tipo: 'capitulo', valor: '' }]);
  const [sending, setSending] = useState(false);

  const updateContent = (i, patch) => setContents(arr => arr.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const addContent = () => setContents(arr => [...arr, { tipo: 'tema', valor: '' }]);
  const removeContent = (i) => setContents(arr => arr.filter((_, idx) => idx !== i));

  const enviar = async () => {
    if (!title.trim()) { toast.error('Informe o título da atividade.'); return; }
    setSending(true);
    try {
      const payload = {
        student_ids: target.alunos.map(a => a.aluno_id),
        class_group_id: target.class_group_id,
        subject_id: target.subject_id,
        subject_name: target.subject_name,
        trimester: target.trimester,
        title: title.trim(),
        instructions: instructions.trim(),
        study_contents: contents.filter(c => (c.valor || '').trim()).map(c => ({ tipo: c.tipo, valor: c.valor.trim() })),
        due_date: dueDate || null,
      };
      let res, json;
      try {
        const r = await api.post('/equipe/recuperacao/classroom', payload);
        res = { ok: true, status: r.status };
        json = r.data;
      } catch (err) {
        res = { ok: false, status: err?.response?.status || 0 };
        json = err?.response?.data || {};
      }
      if (res.ok) {
        toast.success(`Atividade enviada para ${payload.student_ids.length} aluno(s) no Classroom.`);
        onSent();
      } else {
        const detail = json?.detail;
        const msg = (typeof detail === 'string') ? detail : (detail?.message || 'Falha ao enviar para o Classroom.');
        if (res.status === 503) {
          toast.error(`Classroom não configurado: ${msg}`, { duration: 6000 });
        } else if (res.status === 403) {
          toast.error(msg);
        } else if (res.status === 400 && detail?.invalidos) {
          toast.error(`${msg} (${detail.invalidos.length} aluno(s) inválido(s))`);
        } else {
          toast.error(msg);
        }
      }
    } catch (e) {
      toast.error(`Erro ao enviar: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="rec-classroom-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-600" />
            Enviar Recuperação para o Google Classroom
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <div><span className="font-medium">Turma:</span> {target.class_group_name}</div>
            <div><span className="font-medium">Disciplina:</span> {target.subject_name}</div>
            <div><span className="font-medium">Trimestre:</span> {target.trimester}</div>
            <div className="mt-2"><span className="font-medium">Alunos ({target.alunos.length}):</span></div>
            <ul className="mt-1 list-disc list-inside max-h-32 overflow-y-auto text-slate-700">
              {target.alunos.map((a, i) => (<li key={i}>{a.aluno_nome}</li>))}
            </ul>
          </div>

          <div>
            <Label>Título da atividade *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex.: Recuperação 1º Trim — Matemática" data-testid="rec-modal-title" />
          </div>

          <div>
            <Label>Instruções para os alunos</Label>
            <Textarea rows={3} value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Como se preparar, o que será cobrado, formato da prova/trabalho..." data-testid="rec-modal-instructions" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Conteúdos para estudar</Label>
              <Button type="button" size="sm" variant="outline" onClick={addContent} className="gap-1" data-testid="rec-modal-add-content"><Plus className="w-3.5 h-3.5" />Adicionar</Button>
            </div>
            <div className="space-y-2">
              {contents.map((c, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`rec-modal-content-${i}`}>
                  <Select value={c.tipo} onValueChange={(v) => updateContent(i, { tipo: v })}>
                    <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {STUDY_TIPOS.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Input className="flex-1" value={c.valor} onChange={e => updateContent(i, { valor: e.target.value })} placeholder="Detalhe (ex.: Cap. 5 - Equações; pgs 80-95)" />
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeContent(i)} aria-label="Remover">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {contents.length === 0 && (<div className="text-xs text-slate-500">Nenhum conteúdo. Clique em "Adicionar" para incluir.</div>)}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prazo (data limite)</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="rec-modal-due-date" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancelar</Button>
          <Button onClick={enviar} disabled={sending} className="bg-purple-600 hover:bg-purple-700 text-white gap-2" data-testid="rec-modal-enviar-btn">
            <Send className="w-4 h-4" />
            {sending ? 'Enviando...' : 'Enviar para Classroom'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
